import {
  Component,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import { NgSelectComponent } from "@ng-select/ng-select";
import { NavController, NavParams, ViewController } from "ionic-angular";
import {
  DropdownItem,
  Order,
  OrderComponent,
} from "models/order-management/models";
import { OrderFormState } from "pages/orders-entry-om/add-order/order-form-state";
import { OrderDataService } from "providers/order-data-service/order-data-service";
import { Observable, Subject } from "rxjs";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import {
  OrderComponentInterface,
  OrderComponentPropertyUpdate,
  OrderCreateRequestUpdate,
} from "../order-components-interface";

interface SearchData {
  searchTerm: string;
  index: number;
}

@Component({
  selector: "medication-ingredients-scriptsure",
  templateUrl: "medication-ingredients-scriptsure.html",
})
export class MedicationIngredientsScriptsureComponent
  implements OrderComponentInterface
{
  /****************************************************************************
   * Inputs                                                                   *
   ****************************************************************************/
  @Input() orderComponent: OrderComponent;
  @Input() orderCreateRequest: Order;
  @Input() set orderFormState(orderFormState: OrderFormState) {
    this._orderFormState = orderFormState;

    if (this._orderFormState.isCompound !== this.prevIsCompound) {
      this.prevIsCompound = this._orderFormState.isCompound;

      if (this._orderFormState.isCompound) {
        this.ngInputs = [{}, {}];
        // Prefill the name field when isFreeText is true
        if (
          this.orderCreateRequest.nameDescription &&
          this.orderCreateRequest.isCompound
        ) {
          this.textFieldValue = this.orderCreateRequest.nameDescription;
        }
      } else {
        this.textFieldValue = "";
        this.ngInputs = [];
        this.orderCreateRequestEvent.emit({
          deletes: ["nameDescription", "ingredients"],
        });
      }
    }
  }

  /****************************************************************************
   * Outputs                                                                  *
   ****************************************************************************/
  @Output() orderCreateRequestEvent =
    new EventEmitter<OrderCreateRequestUpdate>();
  @Output() orderFormStateEvent = new EventEmitter<OrderFormState>();
  @Output() isValidEvent = new EventEmitter<boolean>();
  @Output() amendedOrderRequestEvent =
    new EventEmitter<OrderComponentPropertyUpdate>();

  /****************************************************************************
   * ViewChild / ViewChildren                                                 *
   ****************************************************************************/
  @ViewChildren(NgSelectComponent)
  ngSelectComponents: QueryList<NgSelectComponent>;
  @ViewChild("medicationNameId") medicationNameInput: NgSelectComponent;
  @ViewChild("hiddenInput") hiddenInput: {
    setFocus: () => void;
  };

  /****************************************************************************
   * State                                                                    *
   ****************************************************************************/
  providerDropdownItems$: Observable<DropdownItem[]>;
  searchSubject: Subject<SearchData> = new Subject();
  dosageStrengths: { label: string; value: string }[] = [];
  frequencyOptions: string[] = [];
  dosageOptions: string[] = [];
  prevIsCompound: boolean = false;
  dosageForm: string;
  dosageRoute: string;
  dosageStrength: string;
  _orderFormState: OrderFormState = {};
  ngInputs: any[] = [];
  textFieldValue: string = "";

  constructor(
    private ordersService: OrderDataService,
    public navCtrl: NavController,
    public navParams: NavParams,
    public viewCtrl: ViewController,
    public stateData: StateManagementService
  ) {}

  get showIngredients() {
    return this._orderFormState.isCompound;
  }

  /****************************************************************************
   * Lifecycle Methods                                                        *
   ****************************************************************************/
  ngOnInit() {
    if (this.isAmendment()) {
      this.setAmendedValue();
    } else if (
      this._orderFormState.formType === "convertToOrder" &&
      this._orderFormState.isCompound &&
      this.orderCreateRequest?.nameDescription
    ) {
      // For convertToOrder with prefilled nameDescription, ensure it's emitted
      this.orderCreateRequestEvent.emit({
        updates: {
          nameDescription: this.orderCreateRequest.nameDescription,
        },
      });
    }
  }

  addNgInput() {
    if (this.ngInputs.length < 10) {
      this.ngInputs.push({});
    }
  }

  removeNgInput(index: number) {
    if (this.orderCreateRequest.ingredients) {
      const prevValueJoin = this.orderCreateRequest.ingredients.join(", ");
      const prevValue = [...this.orderCreateRequest.ingredients];
      this.orderCreateRequest.ingredients.splice(index, 1);
      if (this._orderFormState.formType === "amendment") {
        const amendedObject: OrderComponentPropertyUpdate = { updates: {} };
        amendedObject.updates = {
          propertyName: "ingredients",
          previousValueDisplay: prevValueJoin,
          previousValue: prevValue,
          newValue: this.orderCreateRequest.ingredients,
        };
        this.amendedOrderRequestEvent.emit(amendedObject);
      } else {
        this.orderCreateRequestEvent.emit({
          updates: {
            ingredients: this.orderCreateRequest.ingredients,
          },
        });
      }
    }

    if (this.ngInputs.length > 2) {
      this.ngInputs.splice(index, 1);
    } else {
      this.ngInputs[index].selectedNameDescription = "";
    }
  }

  /****************************************************************************
   * Setter Methods                                                           *
   ****************************************************************************/
  setAmendedValue() {
    this.textFieldValue = this.orderCreateRequest.nameDescription;
    if (this.orderCreateRequest && this.orderCreateRequest.ingredients) {
      this.orderCreateRequest.ingredients.forEach((ingredient, index) => {
        if (index > 1) {
          this.addNgInput();
        }
        this.handleIngredientSelected(ingredient, index);
      });
    }
    this.checkIsValid();
  }

  /****************************************************************************
   * Component Related Methods                                                *
   ****************************************************************************/
  onIngredientSelected(event: string, index: number) {
    if (event) {
      this.handleIngredientSelected(event, index);
    }
  }

  handleIngredientSelected(ingredient: string, index: number): void {
    if (!ingredient) {
      return;
    }
    this.ngInputs[index].selectedNameDescription = ingredient;

    if (!this.orderCreateRequest.ingredients) {
      this.orderCreateRequest.ingredients = [];
    }

    if (this._orderFormState.formType === "amendment") {
      const componentField = this.orderComponent.properties[0].name;
      const prevValue = this.orderCreateRequest[componentField];
      const arrayOfStrings: string[] = [];
      for (const item of this.ngInputs) {
        if (item.selectedNameDescription) {
          arrayOfStrings.push(item.selectedNameDescription);
        }
      }

      if (JSON.stringify(prevValue) !== JSON.stringify(arrayOfStrings)) {
        const prevValueJoin = prevValue.join(", ");
        const amendedObject: OrderComponentPropertyUpdate = { updates: {} };
        amendedObject.updates = {
          propertyName: componentField,
          previousValueDisplay: prevValueJoin,
          previousValue: this.orderCreateRequest[componentField],
          newValue: arrayOfStrings,
        };
        this.amendedOrderRequestEvent.emit(amendedObject);
      } else {
        const amendedObject: OrderComponentPropertyUpdate = {
          updates: {},
          deletes: [componentField as keyof Order],
        };
        this.amendedOrderRequestEvent.emit(amendedObject);
      }
    } else {
      this.orderCreateRequest.ingredients[index] = ingredient;
      // Update request
      if (!this._orderFormState.isCompound) {
        this.orderCreateRequestEvent.emit({
          updates: {
            ingredients: this.orderCreateRequest.ingredients,
            dosageForm: "",
            dosageRoute: "",
            dosageStrength: "",
          },
        });
      } else {
        this.orderCreateRequestEvent.emit({
          updates: {
            ingredients: this.orderCreateRequest.ingredients,
          },
        });
      }
    }
    this.checkIsValid();
  }

  onTextFieldValueChange(event: string) {
    let value = "";
    if (event) {
      value = event;
    }
    const componentField = "nameDescription";
    if (this._orderFormState.formType === "amendment") {
      const prevValue = this.orderCreateRequest.dosageStrength;
      const amendedObject: OrderComponentPropertyUpdate = { updates: {} };
      if (prevValue !== value) {
        amendedObject.updates = {
          propertyName: componentField,
          previousValueDisplay: this.orderCreateRequest[componentField],
          previousValue: this.orderCreateRequest[componentField],
          newValue: value,
        };
        this.amendedOrderRequestEvent.emit(amendedObject);
      } else {
        const amendedObject: OrderComponentPropertyUpdate = {
          updates: {},
          deletes: [componentField as keyof Order],
        };
        this.amendedOrderRequestEvent.emit(amendedObject);
      }
    } else {
      const updateObject: OrderCreateRequestUpdate = { updates: {} };
      updateObject.updates["nameDescription"] = value;
      this.orderCreateRequestEvent.emit(updateObject);
      this.orderFormStateEvent.emit({
        routedMedDescription: value,
      });
    }

    this.checkIsValid();
  }

  checkIsValid() {
    if (!this.textFieldValue || this.textFieldValue.length === 0) {
      this.isValidEvent.emit(false);
    } else {
      this.isValidEvent.emit(true);
    }
  }
  // Hackish fix to show keyboard on IOS / Safari
  onClick(i: number) {
    this.hiddenInput.setFocus();
    let element = this.ngSelectComponents.toArray()[i];
    element.filterInput.nativeElement.focus();
  }

  isAmendment = (): boolean => this._orderFormState.formType === "amendment";

  /****************************************************************************
   * Cypress-Related ("data-cy") Methods                                      *
   ****************************************************************************/
  toKebabCase(label: string): string {
    if (label) {
      return label
        .replace(/^\s+|\s+$/gm, "")
        .replace(/([a-z])([A-Z])/g, "$1-$2")
        .replace(/[\s_]+/g, "-")
        .toLowerCase();
    }
    return "";
  }

  createDataCyAttribute(component: string, option: string): string {
    return `${this.toKebabCase(component)}-option-${this.toKebabCase(option)}`;
  }
}



// WEBPACK FOOTER //
// ./src/components/order-management/medication-ingredients-scriptsure/medication-ingredients-scriptsure.ts